/* eslint-env browser */
import { ApolloClient } from '@apollo/client';
import { withApollo } from '@apollo/client/react/hoc';
import * as Sentry from '@sentry/nextjs';
import { gql } from 'graphql-tag';
import { autorun } from 'mobx';
import Router from 'next/router';
import { posthog, PostHogConfig } from 'posthog-js';
import PropTypes from 'prop-types';
import React, { Component, createContext } from 'react';
import { compose, hoistStatics, withContext as withLegacyContext } from 'recompose';

import { UserAgent } from '../chromatic-lib/constants/UserAgent';
import {
  LAST_TOUCH_COOKIE_NAME,
  mapAttributionToSegmentParams,
} from '../chromatic-lib/events/attribution';
import { EventBundle } from '../chromatic-lib/events/events';
import { IntercomQueryQuery } from './__generated__/withAnalytics.types';
import { campaignAttribution } from './attribution';
import { getTokenSingleton } from './authentication';
import { getCookie } from './cookie';
import { validateIntercomToken } from './intercomAuth';
import { getSessionId } from './withSessionId';

const NO_INTERCOM_REGEXPS = [/\/test/, /^\/$/, /^\/book/, /\/component$/, /\/connect/];

function getLastTouchCampaign() {
  try {
    const lastTouchCookie = getCookie(LAST_TOUCH_COOKIE_NAME);
    if (!lastTouchCookie) return {};
    return { campaign: { ...mapAttributionToSegmentParams(JSON.parse(lastTouchCookie)) } };
  } catch {
    return {};
  }
}

type AnalyticsWithTrackBundle = typeof window.analytics & {
  trackBundle: (eventBundle: EventBundle, userProperties?: Record<string, string>) => void;
};

let identifiedUser: IntercomQueryQuery['me'] | undefined;

// This makes a consistent API to `window.analytics` *AS IT CHANGES*, and
// also adds the `trackBundle` convenience method:
//   https://github.com/hichroma/chromatic/issues/428#issuecomment-365223042
const analytics = {} as AnalyticsWithTrackBundle;
['page', 'identify', 'track', 'reset', 'trackBundle'].forEach((method) => {
  analytics[method] = (...args) => {
    if (!process.browser) {
      throw new Error('Analytics not available on the server');
    }
    // Don't track the capture worker as it costs us money and is not useful
    if (window.navigator.userAgent.match(new RegExp(`${UserAgent.CAPTURE_WORKER}`))) {
      return null;
    }

    if (posthogInitialized) {
      if (method === 'identify') {
        posthog.identify(args[0]);
      }
      if (method === 'reset') {
        posthog.reset();
      }
    }

    if (method === 'trackBundle') {
      const [eventBundle, userProperties] = args as [EventBundle, Record<string, string>];
      const mergedProperties = {
        session_id: getSessionId(),
        ...(identifiedUser && { user_email: identifiedUser.email }),
        ...eventBundle.properties,
        ...userProperties,
      };
      const options = { context: { ...getLastTouchCampaign() } };
      const mergedArgs = [eventBundle.event, mergedProperties, options] as const;
      return window.analytics.track(...mergedArgs);
    }
    return window.analytics[method](...args);
  };
});

function shouldHideIntercom() {
  const hide = !!NO_INTERCOM_REGEXPS.find((re) => !!document.location.pathname.match(re));
  return hide;
}

function page() {
  const sessionId = getSessionId();
  const attr = campaignAttribution();
  attr.storeAttributionValues();

  analytics.page(
    { session_id: sessionId, ...(identifiedUser && { user_email: identifiedUser.email }) },
    {
      integrations: { Intercom: { hideDefaultLauncher: shouldHideIntercom() } },
      context: { ...getLastTouchCampaign() },
    }
  );
  // If Intercom was loaded manually, notify the Intercom widget when to hide itself
  // eslint-disable-next-line no-underscore-dangle
  if (window.__chromatic_intercom_loaded_manually__ && window.Intercom) {
    window.Intercom('update', { hide_default_launcher: shouldHideIntercom() });
  }
}

function identify(user: IntercomQueryQuery['me']) {
  identifiedUser = user;
  const { id } = user;

  validateIntercomToken(id);

  // Sentry is used for error reporting, and we want to be able to correlate
  // errors with users.
  // Sentry is not configured to send events to Segment in the analytics.identify call below.
  Sentry.setUser({ id });

  // This event is simply to associate the user with their anonymous ID
  analytics.identify(id);
}

// we need to wait to pick up the Apollo Client below
let didIndentify = false;
export function ensureIdentifyLoggedInUser(
  client: ApolloClient<any>,
  forceIdentify: boolean = false
) {
  if (didIndentify && !forceIdentify) {
    return;
  }
  didIndentify = true;
  let subscription;
  autorun(() => {
    if (getTokenSingleton().isLoggedIn) {
      subscription = client
        .watchQuery<IntercomQueryQuery>({
          query: gql`
            query IntercomQuery {
              me {
                id
                email
                name
                admin
                apps {
                  id
                  name
                }
              }
            }
          `,
        })
        .subscribe({
          next: ({ data }) => {
            if (!data || !data.me) return;
            if (!process.browser) return;
            identify(data.me);
          },
          error: () => {
            // We can safely ignore these errors; the only one I have seen comes
            // from the store resetting in flight due to logging in/out
          },
        });
    } else {
      // This code block is hit for logged out users that have never auth'd.
      // For this reason, we only want to call analytics.reset() when we know
      // a user is logging out. analytics.reset() clears the anonymous id, so
      // if it is called for all anonymous users, we end up resetting their
      // anonymous ID on page refresh or potentially in between our various
      // properties. This would affect how much we pay Segment every month and
      // mean that we can't correlate anonymous users to all of their events if
      // we accidentally reset their anonymous ID during their journey.
      if (identifiedUser) {
        analytics.reset();
        identifiedUser = undefined;
      }

      if (subscription) {
        subscription.unsubscribe();
      }
    }
  });
}

export const propTypes = {
  analytics: PropTypes.shape({
    trackBundle: PropTypes.func.isRequired,
  }).isRequired,
};

// Wait until a page that uses the below decorator is mounted before turning on page-level
// analytics. Otherwise if this file is required by e.g. a story, we'll start trying to report to
// segment.
let started = false;
function start() {
  if (started) return;

  // @ts-expect-error this is some deprecated Next.js API I think
  Router.onRouteChangeComplete = page;
  page();

  started = true;
}

export const AnalyticsContext = createContext(undefined);

// initialize Posthog
// https://posthog.com/docs/libraries/segment
let posthogInitialized = false;
if (typeof window !== 'undefined' && process.env.POSTHOG_API_KEY && process.env.POSTHOG_HOST) {
  window.analytics.ready(() => {
    posthog.init(process.env.POSTHOG_API_KEY, {
      api_host: process.env.POSTHOG_HOST,
      segment: window.analytics as unknown as PostHogConfig['segment'],
      capture_pageview: false, // You want this false if you are going to use segment's `analytics.page()` for pageviews
      capture_pageleave: true, // Segment does not capture pageleave events, these will bypass segment and go directly to posthog
    });
    posthogInitialized = true;
  });
}

// We are using a HOC to get a reference to the apollo client, once it has been instantiated,
// as well as ensuring we actually want to do page-level analytics (ie we aren't in tests).
export default hoistStatics(
  compose(
    withLegacyContext(propTypes, () => ({ analytics })),
    withApollo,
    (Child) =>
      class Wrapped extends Component<{ client: ApolloClient<any> }> {
        static getInitialProps(context) {
          return Child.getInitialProps ? Child.getInitialProps(context) : {};
        }

        static propTypes = {
          client: PropTypes.shape({ watchQuery: PropTypes.func.isRequired }),
        };

        static defaultProps = {
          client: null,
        };

        componentDidMount() {
          const { client } = this.props;
          ensureIdentifyLoggedInUser(client);
          start();
        }

        render() {
          const { client: _, ...props } = this.props;
          return (
            <AnalyticsContext.Provider value={{ analytics }}>
              {/**
               * Passing analytics down as a prop is deprecated behavior. For new work,
               * use context to access the analytics object in child components.
               * https://github.com/chromaui/chromatic/issues/3572
               */}
              <Child {...props} analytics={analytics} />
            </AnalyticsContext.Provider>
          );
        }
      }
  )
);
